import type { AITestDto, QuestionAggregateDto } from "@mmm/contracts";
import { Card, useT } from "@mmm/ui";
interface TestDocumentPreviewProps {
  subjectTitle: string;
  includeMemorandum?: boolean;
  test: AITestDto;
  questions: QuestionAggregateDto[];
}

const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];

export function TestDocumentPreview({ subjectTitle, test, questions, includeMemorandum = false }: TestDocumentPreviewProps) {
  const t = useT();
  const sortedQuestions = questions.slice().sort((a, b) => a.questionNumber - b.questionNumber);

  return (
    <Card padding="md" className="mmm-export-preview-page">
      <h1 className="mmm-export-document-title">{subjectTitle}</h1>
      <h2 className="mmm-export-test-title">{test.title}</h2>
      <div className="mmm-export-test-questions">
        {sortedQuestions.map((question) => (
          <section className="mmm-export-test-question" key={question.id}>
            <h3>{t("tests.numberedPrompt", { number: question.questionNumber, prompt: question.prompt })}</h3>
            {question.type === "freeText" ? (
              <div className="mmm-export-answer-lines" aria-hidden="true" />
            ) : (
              <ol className="mmm-export-test-options" type="A">
                {question.answerOptions
                  .slice()
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((option) => <li key={option.id}>{option.text}</li>)}
              </ol>
            )}
          </section>
        ))}
      </div>
      {includeMemorandum && <section className="mmm-export-test-memorandum">
        <h2>{t("tests.memorandum")}</h2>
        {sortedQuestions.map((question) => {
          const correctAnswer =
            question.type === "freeText"
              ? question.correctAnswer || ""
              : question.answerOptions
                  .slice()
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((option, index) => (option.isCorrect ? OPTION_LETTERS[index] : null))
                  .filter((letter): letter is string => letter !== null)
                  .join(", ");

          return (
            <div className="mmm-export-test-question" key={question.id}>
              <h3>{t("tests.numberedPrompt", { number: question.questionNumber, prompt: correctAnswer })}</h3>
              <p>{question.explanation}</p>
              {question.rubricItems.length > 0 && (
                <p className="mmm-export-test-rubric">
                  <strong>{t("tests.rubricItems")}: </strong>
                  {question.rubricItems
                    .slice()
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((item) => `${item.text} (${item.points} pt)`)
                    .join("; ")}
                </p>
              )}
            </div>
          );
        })}
      </section>}
    </Card>
  );
}
